import {Injectable} from '@angular/core';
import {ColumnDefinition, VehicleLogRow} from '../../models/vte';
import {hasVteTransaction} from '../../shared/vte/vehicle-log-vte.utils';
import {customerAnalyticsName, isVehicleDetectionAnalyticsKey} from './vision-customer-labels';
import {VisionConfigService} from './vision-config.service';

export type VehicleLogColumnContext = 'dashboard' | 'track' | 'reports' | 'violations';

/**
 * Customer-facing vehicle log tables (Dashboard / Track Vehicle / Reports).
 * Single source of truth for analytics-aware column visibility (Phase 15).
 */
const CUSTOMER_LOG_CONTEXTS: ReadonlySet<VehicleLogColumnContext> = new Set([
    'dashboard',
    'track',
    'reports',
]);

@Injectable({
    providedIn: 'root',
})
export class VehicleLogColumnsService {
    constructor(private visionConfig: VisionConfigService) {
    }

    getBaseColumns(context: VehicleLogColumnContext = 'track'): ColumnDefinition[] {
        const columns: ColumnDefinition[] = [
            {key: 'vehicle_number', label: 'Vehicle Number', cellType: 'text', sortable: true},
            {key: 'direction', label: 'Direction', cellType: 'text', sortable: true},
            {key: 'created_at', label: 'Date & Time', cellType: 'text', sortable: true},
            {key: 'camera_esn_display', label: 'Camera', cellType: 'text'},
        ];

        if (context !== 'dashboard') {
            columns.push({key: 'is_match', label: 'Authorized', cellType: 'text'});
        }

        return columns;
    }

    /**
     * Access stays customer-visible. Inspection outcome is operations/debugging only.
     * Access / inspection columns reflect Vision Verification — only when Vision Mode is active.
     */
    getInspectionColumns(context: VehicleLogColumnContext = 'track'): ColumnDefinition[] {
        if (!this.visionConfig.isVisionActive) {
            return [];
        }
        const columns: ColumnDefinition[] = [
            {key: 'access_decision', label: 'Access', cellType: 'access_decision'},
        ];
        if (!CUSTOMER_LOG_CONTEXTS.has(context)) {
            columns.push({
                key: 'vte_inspection_outcome',
                label: 'Inspection',
                cellType: 'inspection_outcome',
            });
        }
        return columns;
    }

    /**
     * Vehicle Detection / LPR ingestion analytics are pipeline implementation details —
     * omit them from customer Dashboard / Track Vehicle / Reports tables.
     *
     * Enabled verification capabilities always receive a column so Missing / Pending
     * remain visible even when the current page has no fact values yet.
     *
     * Capability-driven columns are not gated by Vision Mode (Phase 15 customer experience).
     * Dashboard, Reports, and Track Vehicle share this method as the single source of truth.
     */
    getAnalyticsFactColumns(
        _rows: VehicleLogRow[] = [],
        context: VehicleLogColumnContext = 'track',
    ): ColumnDefinition[] {
        const customerFacing = CUSTOMER_LOG_CONTEXTS.has(context);
        const hideVehicleDetection = customerFacing;

        return this.visionConfig.enabledAnalytics
            .filter((definition) => definition.output_fact?.trim())
            .filter((definition) =>
                !(hideVehicleDetection && isVehicleDetectionAnalyticsKey(definition.analytics_key)))
            .map((definition) => ({
                key: `fact:${definition.output_fact}`,
                label: customerFacing
                    ? customerAnalyticsName(definition)
                    : definition.name,
                cellType: 'analytics_fact' as const,
                outputFact: definition.output_fact,
                analyticsKey: definition.analytics_key,
                providerId: definition.provider,
            }));
    }

    getColumnsForRows(
        rows: VehicleLogRow[],
        context: VehicleLogColumnContext = 'track',
    ): ColumnDefinition[] {
        return [
            ...this.getBaseColumns(context),
            ...this.getInspectionColumns(context),
            ...this.getAnalyticsFactColumns(rows, context),
        ];
    }

    getColumnsForHeader(context: VehicleLogColumnContext = 'track'): ColumnDefinition[] {
        return [
            ...this.getBaseColumns(context),
            ...this.getInspectionColumns(context),
        ];
    }

    shouldRenderVteRow(row: VehicleLogRow): boolean {
        // Vehicle transactions remain visible regardless of Vision Mode.
        return hasVteTransaction(row);
    }
}
